import { z } from "zod";

export const screenConfigSchema = z.object({
  screen_id: z.number(),
  videos: z.array(z.string()),
  images: z.array(z.string()),
  video_duration: z.number().min(5).max(600),
  slideshow_duration: z.number().min(5).max(600),
  slideshow_interval: z.number().min(1).max(60),
  ticker_text: z.string(),
  ticker_speed: z.number().min(5).max(120),
  ticker_enabled: z.boolean(),
  autorotate: z.boolean(),
  // Screens not yet in service (a new location rolling out screens one at a
  // time) show a plain placeholder instead of a black screen with nothing
  // configured. Optional so older stored configs, which predate this field,
  // default to enabled rather than disappearing.
  enabled: z.boolean().optional(),
  // Web pages / YouTube / TikTok links (see web.ts), each shown for
  // web_duration seconds after the videos and slides. Optional.
  web_urls: z.array(z.string()).optional(),
  web_duration: z.number().min(5).max(600).optional(),
  // Time-of-day content (see schedule.ts). Optional: most screens have none.
  schedules: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        days: z.array(z.number().int().min(0).max(6)),
        start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
        end: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
        videos: z.array(z.string()),
        images: z.array(z.string()),
        web: z.array(z.string()).optional(),
      }),
    )
    .optional(),
  updated_at: z.string(),
});

export type ScreenConfig = z.infer<typeof screenConfigSchema>;

export const screenSchema = z.object({
  id: z.number(),
  name: z.string(),
  orientation: z.enum(["portrait", "landscape"]),
  position: z.string(),
});

export type Screen = z.infer<typeof screenSchema>;

export const screenCommandSchema = z.object({
  id: z.number(),
  screen_id: z.number(),
  command: z.enum(["reload", "clear_cache", "fullscreen", "restart_box", "unlock_kiosk"]),
  created_at: z.string(),
});

export type ScreenCommandRow = z.infer<typeof screenCommandSchema>;

export const ALLOWED_UPLOAD_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "video/mp4",
  "video/webm",
] as const;

export function defaultScreenConfig(screenId: number): Omit<ScreenConfig, "updated_at"> {
  return {
    screen_id: screenId,
    videos: [],
    images: [],
    video_duration: 30,
    slideshow_duration: 20,
    slideshow_interval: 5,
    ticker_text: "",
    ticker_speed: 30,
    ticker_enabled: false,
    autorotate: true,
    enabled: true,
  };
}
